import { useState } from "react";
import { Key, Monitor, Server, Copy, Check, Plus, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useAgentKeys,
  useCreateAgentKey,
  useRevokeAgentKey,
  useDeployAgents,
  useFleetKeys,
  useCreateFleetKey,
  useRevokeFleetKey,
  useFleetKeyHosts,
} from "@/hooks/use-api";
import { formatRelativeTime } from "@/lib/formatters";
import type {
  AgentKey,
  AgentOSType,
  DeployAgent,
  FleetKey,
  FleetKeyMint,
  FleetRmmType,
  HealthMetrics,
} from "@/types/api";

// ── Helpers ────────────────────────────────────────────────

// Window after which an `is_active=true` key with no recent heartbeat is
// downgraded to `idle`. Yosef-2026-05-07: a 10-second-old wizard token that
// was never installed showed the same badge as a key currently ingesting
// from a real host; he could not tell them apart in the keys table. 15
// minutes is wider than the agent's 60s heartbeat interval (no false-idle
// flicker) and narrower than the 14d test/demo sweep (keys that go idle
// for the sweep window are visually distinct from "actively heartbeating
// right now").
const KEY_IDLE_THRESHOLD_MS = 15 * 60 * 1000;

export type AgentKeyStatus = "active" | "idle" | "revoked";

// Scores any key carrying is_active + last_used_at (AgentKey or FleetKey) into
// the same active/idle/revoked badge.
export function deriveKeyStatus(
  key: Pick<AgentKey, "is_active" | "last_used_at">,
  now: number = Date.now(),
): AgentKeyStatus {
  if (!key.is_active) return "revoked";
  if (!key.last_used_at) return "idle";
  const lastUsedMs = new Date(key.last_used_at).getTime();
  if (Number.isNaN(lastUsedMs)) return "idle";
  return now - lastUsedMs <= KEY_IDLE_THRESHOLD_MS ? "active" : "idle";
}

function StatusBadge({ status }: { status: AgentKeyStatus }) {
  const styles: Record<AgentKeyStatus, string> = {
    active: "bg-green-500/20 text-green-400",
    idle: "bg-muted text-muted-foreground",
    revoked: "bg-red-500/20 text-red-400",
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {status}
    </span>
  );
}

// Purpose styling — prod stays neutral (the default; no visual noise on
// normal-case rows). Test/demo keys auto-expire at 14d idle (#82), so we
// pill them in amber so an operator eyeballing the keys list sees the
// short-lived lifecycle at a glance.
function PurposePill({ purpose }: { purpose: AgentKey["purpose"] }) {
  if (!purpose || purpose === "prod") {
    return <span className="text-xs text-muted-foreground">prod</span>;
  }
  return (
    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-400">
      {purpose}
    </span>
  );
}

function AgentStatusDot({ status }: { status: "active" | "inactive" | "offline" }) {
  const colors = {
    active: "bg-green-500",
    inactive: "bg-yellow-500",
    offline: "bg-red-500",
  };
  return (
    <span
      className={`inline-block h-2 w-2 rounded-full ${colors[status]}`}
      title={status}
    />
  );
}

// ── Host health metrics (#625) ────────────────────────────

// Metrics older than this render muted: 3× the heartbeat interval, same
// shape as KEY_IDLE_THRESHOLD_MS above. The server COALESCE-keeps the last
// snapshot when a heartbeat arrives without metrics, so values can age
// even while heartbeats stay fresh — old data must not claim freshness.
const METRICS_STALE_THRESHOLD_MS = 15 * 60 * 1000;

export function metricsAreStale(at: string | null, now: number = Date.now()): boolean {
  if (!at) return true;
  const ts = new Date(at).getTime();
  if (Number.isNaN(ts)) return true;
  return now - ts > METRICS_STALE_THRESHOLD_MS;
}

export function usedPct(
  total: number | null | undefined,
  free: number | null | undefined,
): number | null {
  if (typeof total !== "number" || typeof free !== "number") return null;
  if (!Number.isFinite(total) || !Number.isFinite(free)) return null;
  if (total <= 0 || free > total) return null;
  const pct = ((total - free) / total) * 100;
  return Math.min(Math.max(pct, 0), 100);
}

export function cpuPct(metrics: HealthMetrics | null): number | null {
  const v = metrics?.cpu_pct;
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return Math.min(Math.max(v, 0), 100);
}

function MetricCell({
  pct,
  stale,
  asOf,
}: {
  pct: number | null;
  stale: boolean;
  asOf: string | null;
}) {
  if (pct === null) {
    return <span className="text-muted-foreground">—</span>;
  }
  // Threshold the SAME integer we display, so a value that rounds up to 90%
  // (e.g. 89.6) shows red rather than amber-on-a-90%-label.
  const shown = Math.round(pct);
  // Staleness wins over thresholds: a stale 95% renders muted, not red —
  // old data makes no freshness claim.
  let cls = "";
  if (stale) {
    cls = "text-muted-foreground/60";
  } else if (shown >= 90) {
    cls = "text-red-400";
  } else if (shown >= 80) {
    cls = "text-amber-400";
  }
  return (
    <span className={cls} title={stale && asOf ? `Metrics as of ${asOf}` : undefined}>
      {shown}%
    </span>
  );
}

function AgentMetricCells({ agent }: { agent: DeployAgent }) {
  const stale = metricsAreStale(agent.health_metrics_at);
  const hm = agent.health_metrics;
  return (
    <>
      <TableCell className="text-sm">
        <MetricCell
          pct={usedPct(hm?.disk_total_bytes, hm?.disk_free_bytes)}
          stale={stale}
          asOf={agent.health_metrics_at}
        />
      </TableCell>
      <TableCell className="text-sm">
        <MetricCell
          pct={usedPct(hm?.mem_total_bytes, hm?.mem_available_bytes)}
          stale={stale}
          asOf={agent.health_metrics_at}
        />
      </TableCell>
      <TableCell className="text-sm">
        <MetricCell pct={cpuPct(hm)} stale={stale} asOf={agent.health_metrics_at} />
      </TableCell>
    </>
  );
}

// ── Raw key reveal card ───────────────────────────────────

function RawKeyCard({
  rawKey,
  onDismiss,
}: {
  rawKey: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(rawKey).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard API unavailable (non-HTTPS or permission denied) — no-op
    });
  }

  return (
    <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-yellow-400">
          Save this key now — it will not be shown again.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 rounded bg-background/60 px-3 py-2 text-xs font-mono break-all">
          {rawKey}
        </code>
        <Button variant="outline" size="sm" aria-label="Copy to clipboard" onClick={handleCopy} className="shrink-0">
          {copied ? (
            <Check className="h-3.5 w-3.5 text-green-400" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </Button>
      </div>
      <Button size="sm" variant="secondary" onClick={onDismiss}>
        I&apos;ve saved this key
      </Button>
    </div>
  );
}

// ── Create key inline form ────────────────────────────────

function CreateKeyForm({ onCancel }: { onCancel: () => void }) {
  const [name, setName] = useState("");
  const [expiryDays, setExpiryDays] = useState("");
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createKey = useCreateAgentKey();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    createKey.mutate(
      {
        name: name.trim(),
        ...(expiryDays ? { expires_in_days: Number(expiryDays) } : {}),
      },
      {
        onSuccess: (data) => {
          if (data.key.raw_key) {
            setRawKey(data.key.raw_key);
          }
        },
        onError: (err) => {
          setError(err instanceof Error ? err.message : "Failed to create key");
        },
      },
    );
  }

  if (rawKey) {
    return (
      <RawKeyCard
        rawKey={rawKey}
        onDismiss={() => {
          setRawKey(null);
          onCancel();
        }}
      />
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-muted/30 p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">New Agent Key</p>
        <Button type="button" variant="ghost" size="icon" aria-label="Cancel" className="h-7 w-7" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {error && (
        <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="key-name" className="text-xs">
            Key Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="key-name"
            placeholder="e.g. Production servers"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="key-expiry" className="text-xs">
            Expires in days{" "}
            <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="key-expiry"
            type="number"
            min={1}
            placeholder="Never"
            value={expiryDays}
            onChange={(e) => setExpiryDays(e.target.value)}
            className="h-8 text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          disabled={!name.trim() || createKey.isPending}
        >
          {createKey.isPending ? "Creating…" : "Create Key"}
        </Button>
      </div>
    </form>
  );
}

// ── Keys table ────────────────────────────────────────────

function KeysTable({ keys }: { keys: AgentKey[] }) {
  const revokeKey = useRevokeAgentKey();

  function handleRevoke(keyId: string, keyName: string) {
    if (window.confirm(`Revoke key "${keyName}"? This cannot be undone.`)) {
      revokeKey.mutate(keyId);
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Instance</TableHead>
          <TableHead>Prefix</TableHead>
          <TableHead>Purpose</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="hidden md:table-cell">Last Used</TableHead>
          <TableHead className="hidden md:table-cell">Expires</TableHead>
          <TableHead className="w-[80px]" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {keys.map((key) => (
          <TableRow key={key.id}>
            <TableCell className="font-medium">{key.name}</TableCell>
            <TableCell className="text-sm">
              {key.instance_label ? (
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  {key.instance_label}
                </code>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell>
              {/* Always show the key prefix — for legacy and non-wizard
                  rows (MSP / RMM / inline create) the prefix is the only
                  stable UI discriminator. Codex P2 r1 on #200: dropping
                  this column when adding Instance left two no-label
                  rows indistinguishable from each other. */}
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                {key.key_prefix}…
              </code>
            </TableCell>
            <TableCell>
              <PurposePill purpose={key.purpose} />
            </TableCell>
            <TableCell>
              <StatusBadge status={deriveKeyStatus(key)} />
            </TableCell>
            <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
              {key.last_used_at ? formatRelativeTime(key.last_used_at) : "Never"}
            </TableCell>
            <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
              {key.expires_at ? formatRelativeTime(key.expires_at) : "Never"}
            </TableCell>
            <TableCell>
              {key.is_active && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs text-destructive hover:text-destructive"
                  onClick={() => handleRevoke(key.id, key.name)}
                  disabled={revokeKey.isPending}
                >
                  Revoke
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ── Fleet keys (#180-C) ───────────────────────────────────

// Display labels for the RMM flavours the fleet-mint endpoint renders. Keys
// mirror the server's FLEET_RMM_TYPES; `generic` is intentionally absent.
const FLEET_RMM_LABELS: Record<FleetRmmType, string> = {
  connectwise_automate: "ConnectWise Automate",
  datto_rmm: "Datto RMM",
  ninjaone: "NinjaOne",
};

const FLEET_OS_TYPES: AgentOSType[] = ["linux", "macos", "windows"];

// Copy-to-clipboard button. The clipboard write can reject on non-HTTPS / denied
// permission; swallow it so a failed copy never throws into the render tree.
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  }
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      aria-label={`Copy ${label}`}
      onClick={handleCopy}
      className="shrink-0"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

// One-time reveal of a freshly minted fleet key. The raw key, install commands,
// and RMM packages are returned exactly once by the API — re-mint is the only
// recovery path — so the card leads with that warning.
function FleetKeyMintReveal({ mint, onDone }: { mint: FleetKeyMint; onDone: () => void }) {
  const rmmTypes = Object.keys(mint.rmm_packages) as FleetRmmType[];
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Fleet key created — save it now</DialogTitle>
        <DialogDescription>
          This key and its install commands will not be shown again. If you lose them, mint a
          new fleet key.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-1.5">
        <Label className="text-xs">Fleet key</Label>
        <div className="flex items-center gap-2 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
          <code className="flex-1 break-all font-mono text-xs">{mint.raw_key}</code>
          <CopyButton value={mint.raw_key} label="fleet key" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Install commands</Label>
        <div className="space-y-2">
          {FLEET_OS_TYPES.map((os) => (
            <div key={os} className="flex items-center gap-2">
              <span className="w-16 shrink-0 text-xs capitalize text-muted-foreground">{os}</span>
              <code className="flex-1 break-all rounded bg-muted px-2 py-1.5 font-mono text-xs">
                {mint.install_cmds[os]}
              </code>
              <CopyButton value={mint.install_cmds[os]} label={`${os} install command`} />
            </div>
          ))}
        </div>
      </div>

      {rmmTypes.length > 0 && (
        <div className="space-y-1.5">
          <Label className="text-xs">RMM packages</Label>
          <Tabs defaultValue={rmmTypes[0]}>
            <TabsList>
              {rmmTypes.map((t) => (
                <TabsTrigger key={t} value={t} className="text-xs">
                  {FLEET_RMM_LABELS[t] ?? t}
                </TabsTrigger>
              ))}
            </TabsList>
            {rmmTypes.map((t) => (
              <TabsContent key={t} value={t} className="space-y-3">
                {FLEET_OS_TYPES.map((os) => {
                  const pkg = mint.rmm_packages[t]?.[os];
                  if (!pkg) return null;
                  return (
                    <div key={os} className="space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium capitalize">{os}</span>
                        <CopyButton value={pkg.package} label={`${FLEET_RMM_LABELS[t] ?? t} ${os} package`} />
                      </div>
                      <p className="text-xs text-muted-foreground">{pkg.instructions}</p>
                      <pre className="max-h-40 overflow-auto rounded bg-background/60 p-2 text-xs">
                        <code>{pkg.package}</code>
                      </pre>
                    </div>
                  );
                })}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      )}

      <DialogFooter>
        <Button size="sm" onClick={onDone}>
          Done
        </Button>
      </DialogFooter>
    </div>
  );
}

// Create-fleet-key modal. Name-only form; on success the same dialog swaps to
// the one-time reveal so the secret never leaves this modal's lifecycle.
function CreateFleetKeyDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [minted, setMinted] = useState<FleetKeyMint | null>(null);
  const [error, setError] = useState<string | null>(null);
  const createKey = useCreateFleetKey();

  function reset() {
    setName("");
    setMinted(null);
    setError(null);
  }

  function close() {
    reset();
    onOpenChange(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    setError(null);
    createKey.mutate(
      { name: name.trim() },
      {
        onSuccess: (data) => setMinted(data.fleet_key),
        onError: (err) =>
          setError(err instanceof Error ? err.message : "Failed to create fleet key"),
      },
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Reset on any close so a reopen starts clean and the revealed secret
        // doesn't linger in state.
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-2xl">
        {minted ? (
          <FleetKeyMintReveal mint={minted} onDone={close} />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Create Fleet Key</DialogTitle>
              <DialogDescription>
                One key deploys agents across many hosts via install command or RMM. The key and
                install commands are shown once.
              </DialogDescription>
            </DialogHeader>

            {error && (
              <div className="rounded border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="fleet-key-name" className="text-xs">
                Key Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fleet-key-name"
                placeholder="e.g. Acme Corp fleet"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-8 text-sm"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!name.trim() || createKey.isPending}>
                {createKey.isPending ? "Creating…" : "Create Fleet Key"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Host list for an expanded fleet key. Mounted only while the row is open, so
// the fetch happens at expand time (no polling in Phase 1; collapse + re-expand
// refetches).
function FleetKeyHosts({ keyId }: { keyId: string }) {
  const { data, isLoading, isError } = useFleetKeyHosts(keyId);
  const hosts: DeployAgent[] = data?.agents ?? [];

  if (isLoading) {
    return (
      <div className="space-y-2 border-t border-border p-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="border-t border-border px-3 py-2 text-xs text-red-400">
        Failed to load hosts for this key.
      </div>
    );
  }
  if (hosts.length === 0) {
    return (
      <div className="border-t border-border px-3 py-3 text-xs text-muted-foreground">
        No hosts have deployed under this key yet.
      </div>
    );
  }

  return (
    <div className="border-t border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Hostname</TableHead>
            <TableHead>OS</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Version</TableHead>
            <TableHead className="hidden md:table-cell">Last Heartbeat</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {hosts.map((host) => (
            <TableRow key={host.id}>
              <TableCell className="font-mono text-sm font-medium">{host.hostname}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{host.os_type}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <AgentStatusDot status={host.status} />
                  <span className="text-sm capitalize">{host.status}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {host.agent_version ?? "—"}
              </TableCell>
              <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                {host.last_heartbeat_at ? formatRelativeTime(host.last_heartbeat_at) : "Never"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function FleetKeyRow({ fleetKey }: { fleetKey: FleetKey }) {
  const [expanded, setExpanded] = useState(false);
  const revokeKey = useRevokeFleetKey();

  function handleRevoke() {
    if (
      window.confirm(
        `Revoke fleet key "${fleetKey.name}"? Hosts deploying under it will stop authenticating. This cannot be undone.`,
      )
    ) {
      revokeKey.mutate(fleetKey.id);
    }
  }

  return (
    <div className="rounded-lg border border-border">
      <div className="flex items-center gap-3 p-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate font-medium">{fleetKey.name}</span>
          <code className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs">
            {fleetKey.key_prefix}…
          </code>
        </button>
        <span className="hidden shrink-0 text-sm text-muted-foreground sm:inline">
          {fleetKey.active_host_count} active / {fleetKey.host_count} total hosts
        </span>
        <StatusBadge status={deriveKeyStatus(fleetKey)} />
        {fleetKey.is_active && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-xs text-destructive hover:text-destructive"
            onClick={handleRevoke}
            disabled={revokeKey.isPending}
          >
            Revoke
          </Button>
        )}
      </div>
      {expanded && <FleetKeyHosts keyId={fleetKey.id} />}
    </div>
  );
}

function FleetKeysSection() {
  const [showCreate, setShowCreate] = useState(false);
  const { data, isLoading, isError } = useFleetKeys();
  const fleetKeys = data?.fleet_keys ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-muted-foreground">Fleet Keys</h3>
          <p className="text-xs text-muted-foreground/70">
            One key, many hosts — for MSP-scale deployment via install command or RMM.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Create Fleet Key
        </Button>
      </div>

      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load fleet keys.
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : fleetKeys.length === 0 && !isError ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
          <div className="rounded-full bg-muted p-3">
            <Server className="h-6 w-6 text-muted-foreground" />
          </div>
          <p className="mt-4 text-sm font-semibold">No fleet keys yet</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Create a fleet key to deploy agents across many hosts with one credential.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {fleetKeys.map((fk) => (
            <FleetKeyRow key={fk.id} fleetKey={fk} />
          ))}
        </div>
      )}

      <CreateFleetKeyDialog open={showCreate} onOpenChange={setShowCreate} />
    </div>
  );
}

// ── Main component ────────────────────────────────────────

export function AgentsTab() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [keysExpanded, setKeysExpanded] = useState(false);

  const { data: keysData, isLoading: keysLoading, isError: keysError } = useAgentKeys();
  const { data: agentsData, isLoading: agentsLoading, isError: agentsError } = useDeployAgents();

  const keys = keysData?.keys ?? [];
  const agents = agentsData?.agents ?? [];

  // Collapse-by-default only kicks in once the operator has ≥1 key — at that
  // point the keys list is reference material, not the primary thing on the
  // tab. With zero keys (or while loading them, or when the create form is
  // open) the section stays open so the empty-state guidance / form is
  // visible without a click.
  const keysListOpen = keysExpanded || keys.length === 0 || showCreateForm;

  return (
    <div className="space-y-8">
      {/* ── Deployed Agents — the live operational view leads ── */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">
          Deployed Agents
        </h3>

        {agentsError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
            Failed to load deployed agents.
          </div>
        )}

        {agentsLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : agents.length === 0 && !agentsError ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
            <div className="rounded-full bg-muted p-3">
              <Monitor className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="mt-4 text-sm font-semibold">No agents reporting</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Create an agent key below, then install an agent on a host to
              see it here.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>OS</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Disk</TableHead>
                <TableHead>Mem</TableHead>
                <TableHead>CPU</TableHead>
                <TableHead className="hidden md:table-cell">Last Heartbeat</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id}>
                  <TableCell className="font-medium font-mono text-sm">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate">{agent.hostname}</span>
                      {agent.install_id && (
                        <span
                          title={agent.install_id}
                          className="shrink-0 text-xs text-muted-foreground"
                        >
                          #{agent.install_id.slice(-8)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {agent.os_type}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <AgentStatusDot status={agent.status} />
                      <span className="text-sm capitalize">{agent.status}</span>
                    </div>
                  </TableCell>
                  <AgentMetricCells agent={agent} />
                  <TableCell className="hidden text-sm text-muted-foreground md:table-cell">
                    {agent.last_heartbeat_at
                      ? formatRelativeTime(agent.last_heartbeat_at)
                      : "Never"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Fleet Keys — MSP-scale one-key-many-hosts (#180-C) ── */}
      <FleetKeysSection />

      {/* ── Agent API Keys — one-time setup, collapsible below ── */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          {keys.length > 0 ? (
            <button
              type="button"
              onClick={() => setKeysExpanded((v) => !v)}
              aria-expanded={keysListOpen}
              className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {keysListOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Agent API Keys
              <span className="text-xs text-muted-foreground/70">({keys.length})</span>
            </button>
          ) : (
            <h3 className="text-sm font-medium text-muted-foreground">
              Agent API Keys
            </h3>
          )}
          {!showCreateForm && (
            <Button
              size="sm"
              onClick={() => {
                setShowCreateForm(true);
                setKeysExpanded(true);
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Create Key
            </Button>
          )}
        </div>

        {keysListOpen && (
          <>
            {showCreateForm && (
              <CreateKeyForm onCancel={() => setShowCreateForm(false)} />
            )}

            {keysError && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
                Failed to load agent keys.
              </div>
            )}

            {keysLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : keys.length === 0 && !keysError ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-12">
                <div className="rounded-full bg-muted p-3">
                  <Key className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="mt-4 text-sm font-semibold">No agent keys yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Create a key to authenticate agents reporting to this tenant.
                </p>
              </div>
            ) : (
              <KeysTable keys={keys} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
